import type { components, operations } from "../../../api/generated";
import type {
  MySqlQueryListItem,
  MySqlQueryListPage,
  MySqlQueryListParams,
  MySqlQueryRecordMutation,
} from "../mysql-query-list/types";

/** 支持的数据库类型。 */
export type DatabaseType = "sqlserver" | "mysql";

type MySqlQueryListContractQuery =
  NonNullable<operations["mysql_query_list_demo_demo_mysql_query_list_get"]["parameters"]["query"]>;
type DemoQueryRecordMutationContract = components["schemas"]["DemoQueryRecordMutation"];
export type MySqlQueryListItemRaw = components["schemas"]["DemoQueryListItem"];
export type MySqlQueryListPageRaw = components["schemas"]["DemoQueryListPage"];

/** 前端消费的数据库连通性数据。 */
export type DatabaseConnectivityResult = {
  customerId: string;
  databaseName: string;
  serverUtcTime: string;
  databaseType: DatabaseType;
  traceId: string;
  sessionId: string;
  elapsedMs: number;
};

/** 兼容旧命名的结果类型。 */
export type SqlServerConnectivityResult = DatabaseConnectivityResult;

/** 兼容旧命名的主键参数类型。 */
export type MySqlQueryRecordId =
  operations["update_mysql_query_record_demo_demo_mysql_query_records__record_id__put"]["parameters"]["path"]["record_id"];

/** 后端数据库连通性原始结构。 */
export type DatabaseConnectivityRaw = components["schemas"]["DatabaseConnectivityPayload"];

/** 后端删除返回原始结构。 */
export type MySqlQueryRecordDeleteResultRaw = components["schemas"]["DemoQueryRecordDeleteResult"];

/** 将后端 snake_case 数据转换为前端 camelCase。 */
export function mapConnectivityPayload(
  raw: DatabaseConnectivityRaw,
  databaseType: DatabaseType,
): DatabaseConnectivityResult {
  return {
    customerId: raw.customer_id,
    databaseName: raw.database_name,
    databaseType,
    elapsedMs: raw.elapsed_ms,
    serverUtcTime: raw.server_utc_time,
    sessionId: raw.session_id,
    traceId: raw.trace_id,
  };
}

/** 将后端列表分页结果转换为前端结构。 */
export function mapMySqlQueryListPage(raw: MySqlQueryListPageRaw): MySqlQueryListPage {
  return {
    items: (raw.items ?? []).map((item) => mapMySqlQueryListItem(item)),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 将前端 CRUD 表单参数转换为后端 snake_case 结构。 */
export function mapMySqlQueryRecordMutationPayload(
  payload: MySqlQueryRecordMutation,
): DemoQueryRecordMutationContract {
  return {
    record_name: payload.recordName,
    source: payload.source,
    tags: payload.tags,
    biz_date: payload.bizDate,
    amount: payload.amount,
    enabled: payload.enabled,
  };
}

/** 将前端列表查询参数转换为契约定义的 query 参数结构。 */
export function mapMySqlQueryListQueryParams(params: MySqlQueryListParams): MySqlQueryListContractQuery {
  return {
    page: params.page,
    page_size: params.pageSize,
    record_name: params.recordName,
    source: params.source,
    tags: params.tags,
    biz_date: params.bizDate,
    created_from: params.createdFrom,
    created_to: params.createdTo,
    enabled: params.enabled,
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 将后端列表项结构统一转换为前端结构。 */
export function mapMySqlQueryListItem(item: MySqlQueryListItemRaw): MySqlQueryListItem {
  return {
    id: item.id,
    recordName: item.record_name,
    source: item.source,
    sourceLabel: item.source_label,
    tags: item.tags ?? [],
    tagsLabel: item.tags_label ?? [],
    bizDate: item.biz_date,
    amount: normalizeNumberValue(item.amount),
    enabled: item.enabled,
    enabledLabel: item.enabled_label,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

/** 将后端返回的数值安全转换为 number，异常值统一回退为 0。 */
function normalizeNumberValue(value: number | string | null): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : 0;
  }

  return 0;
}
